import { workspaceLink, workspaceMenu } from './workspaceNavigation'

export const grantKeys = [
  'home_work',
  'marketing',
  'contracts',
  'projects',
  'scheduling',
  'campaigns',
  'blast_history',
  'legal',
  'call_tools',
] as const
export type WorkspaceGrant = (typeof grantKeys)[number]
type LegacyModule = components['schemas']['ModulesEnum']
const legacyGrants: Record<LegacyModule, WorkspaceGrant[]> = {
  manager: ['home_work', 'contracts', 'projects', 'legal'],
  crm: ['marketing', 'campaigns'],
  comms: ['blast_history', 'call_tools'],
  scheduling: ['scheduling'],
  interactions: [],
}

export function assignedGrants(
  role?: {
    modules?: LegacyModule[]
    workspace_access?: WorkspaceGrant[]
  } | null
) {
  return new Set<WorkspaceGrant>(
    role?.workspace_access ??
      (role?.modules ?? []).flatMap((key) => legacyGrants[key] ?? [])
  )
}

// Keep the moved actions in the same groups as their sidebar links.
export const workspaceAccessGroups = workspaceMenu.map((group) => {
  const items = group.items.map(({ label, access }) => ({ label, access }))
  if (group.label === 'Campaigns')
    items.push(
      { label: 'Contact lists', access: 'campaigns' },
      { label: 'Blast history and sending', access: 'blast_history' }
    )
  return {
    label: group.label,
    items,
    grants: [
      ...new Set(
        items.flatMap((item) =>
          item.access && item.access !== 'admin' ? [item.access] : []
        )
      ),
    ],
  }
})

export function workspaceAccessSummary(
  grants: Set<WorkspaceGrant>,
  admin: boolean
) {
  return workspaceAccessGroups
    .map((group) => ({
      label: group.label,
      partial:
        !admin &&
        group.items.some(
          (item) =>
            item.access && (item.access === 'admin' || !grants.has(item.access))
        ),
      items: group.items
        .filter(
          (item) =>
            admin ||
            !item.access ||
            (item.access !== 'admin' && grants.has(item.access))
        )
        .map((item) => item.label),
    }))
    .filter((group) => group.items.length)
}

export function routeGrant(fullPath: string) {
  const path = fullPath.split(/[?#]/)[0]!.replace(/\/+$/, '')
  if (path === '/leads/tracking/settings') return 'admin'
  // Crews' GPS routes: admins only, like the API.
  if (path === '/scheduling/time-tracking') return 'admin'
  if (/^\/comms\/blasts(?:\/|$)/.test(path)) return 'blast_history'
  if (/^\/crm\/(?:leads|contact-lists)(?:\/|$)/.test(path) || path === '/crm')
    return 'campaigns'
  if (path === '/comms' || path === '/comms/conversations') return 'call_tools'
  if (path === '/manager') return 'projects'
  if (path === '/scheduling' || path.startsWith('/scheduling/'))
    return 'scheduling'
  return workspaceLink(fullPath)?.item.access
}
